import { Fragment, type ReactNode } from "react";

/**
 * Small typesetting for the reading card (2026-11-03). Two habits of a good news desk:
 * a compound ("18-year", "cost-of-living") never breaks at its hyphen across a headline, and
 * the figures in the key points stand a little darker than the words around them, so a
 * skimming eye lands on "4.85 per cent" before it reads the sentence. Pure splitting is
 * exported for tests; rendering only wraps, it never changes a character.
 */

const COMPOUND = /[\p{L}\p{N}$]+(?:[-‑][\p{L}\p{N}]+)+/gu;

// A figure: an optional currency, digits with thousands separators and decimals, and the unit
// that belongs to it. "per cent" travels with its number; a bare year (1900 to 2099) is a date,
// not a figure, and stays in the sentence's own weight.
const FIGURE = /(?:A?\$|£|€)?\d[\d,]*(?:\.\d+)?(?:\s?(?:%|per cent|percentage points?|bn|billion|million|trillion|m\b|k\b))?/g;
const BARE_YEAR = /^(?:19|20)\d{2}$/;

export type Run = { text: string; mark: boolean };

function split(text: string, re: RegExp, keep: (m: string) => boolean = () => true): Run[] {
  const runs: Run[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const at = m.index ?? 0;
    if (!m[0] || !keep(m[0])) continue;
    if (at > last) runs.push({ text: text.slice(last, at), mark: false });
    runs.push({ text: m[0], mark: true });
    last = at + m[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last), mark: false });
  return runs;
}

/** The text cut into plain runs and hyphenated compounds. */
export function compoundRuns(text: string): Run[] {
  return split(text || "", COMPOUND);
}

/** The text cut into plain runs and figures (bare years excluded). */
export function figureRuns(text: string): Run[] {
  return split(text || "", FIGURE, (m) => /\d/.test(m) && !BARE_YEAR.test(m.trim()));
}

/** A headline whose compounds hold together. */
export function keepCompounds(text: string): ReactNode {
  return compoundRuns(text).map((r, i) =>
    r.mark ? <span className="nobr" key={i}>{r.text}</span> : <Fragment key={i}>{r.text}</Fragment>,
  );
}

/** A key point whose figures stand out. */
export function markFigures(text: string): ReactNode {
  return figureRuns(text).map((r, i) =>
    r.mark ? <b className="fig" key={i}>{r.text}</b> : <Fragment key={i}>{r.text}</Fragment>,
  );
}
